import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Flex,
  IconButton,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  Tooltip,
  Badge,
  useColorModeValue,
  Portal,
  Image,
  SimpleGrid,
  Heading,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  Input,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Select,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Icon,
} from '@chakra-ui/react';
import {
  FaPlus,
  FaCog,
  FaPaintBrush,
  FaLayerGroup,
  FaCube,
  FaRegEye,
  FaAngleLeft,
  FaAngleRight,
  FaTrash,
  FaGripVertical,
  FaChevronDown,
  FaChevronUp,
  FaMagic,
  FaRegSave,
  FaFileAlt,
  FaFileImport,
  FaExternalLinkAlt,
  FaBolt,
  FaFilter,
  FaBoxOpen,
  FaSearch,
  FaShapes,
  FaCopy,
  FaPaste,
  FaUndo,
  FaRedo,
  FaThLarge,
  FaNewspaper,
  FaPhotoVideo,
  FaGamepad,
  FaFutbol,
  FaSmile,
  FaExclamationCircle
} from 'react-icons/fa';
import {
  FiMonitor,
  FiTablet,
  FiSmartphone,
  FiSave,
  FiX,
  FiEdit,
  FiMove,
  FiChevronUp,
  FiChevronDown,
  FiTrash2,
  FiEyeOff,
  FiSettings,
  FiLock,
  FiAlertTriangle
} from 'react-icons/fi';
import {
  PageElementConfig,
  getPageElementConfigs,
  batchUpdatePageElementConfigs,
  ELEMENT_VARIANTS,
  ElementVariant,
  PREDEFINED_ELEMENTS,
  PredefinedElement,
} from '../../services/pageElements';
import api from '../../services/api';
import { safeDOM } from '../../services/myuibrix';
import { useAuth } from '../../contexts/AuthContext';
import { useClubTheme } from '../../contexts/ClubThemeContext';
import VisualStylePanel from './VisualStylePanel';
import { DEFAULT_HOMEPAGE_ELEMENTS, HOMEPAGE_IMPLEMENTED_ELEMENTS } from '../../data/defaultElements';

const SUPPORTED_HOME_VARIANTS: Record<string, string[]> = {
  hero: ['grid', 'scroller', 'swiper', 'swiper_full'],
  news: ['grid_one', 'grid_two', 'grid', 'list', 'scroller'],
  matches: ['compact'],
  sponsors: ['grid', 'slider', 'scroller', 'logos3', 'pyramid'],
  gallery: ['grid'],
  videos: ['grid', 'carousel'],
  merch: ['grid'],
  table: ['split_news'],
  banner: ['top'],
  sidebar: ['right'],
  newsletter: ['default'],
};

interface MyUIbrixStyleEditorProps {
  pageType: string;
  onConfigChange?: (configs: PageElementConfig[]) => void;
}

interface ElementPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

const MyUIbrixStyleEditor: React.FC<MyUIbrixStyleEditorProps> = ({ pageType, onConfigChange }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const clubTheme = useClubTheme();
  
  const [isEditing, setIsEditing] = useState(false);
  const [configs, setConfigs] = useState<PageElementConfig[]>([]);
  const [localChanges, setLocalChanges] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [hoveredElement, setHoveredElement] = useState<string | null>(null);
  const [elementPosition, setElementPosition] = useState<ElementPosition | null>(null);
  const [showElementPicker, setShowElementPicker] = useState(false);
  const [showLayersPanel, setShowLayersPanel] = useState(false);
  const [showElementsPanel, setShowElementsPanel] = useState(false);
  const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());
  const [elementOrder, setElementOrder] = useState<string[]>([]);
  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [dragOverElement, setDragOverElement] = useState<string | null>(null);
  const [viewport, setViewport] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [elementStyles, setElementStyles] = useState<Record<string, any>>({});
  const [showStylePanel, setShowStylePanel] = useState(false);
  const [stylePanelRight, setStylePanelRight] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showHelpHint, setShowHelpHint] = useState(true);
  const [baseline, setBaseline] = useState<{ variants: Record<string, string>; visible: Set<string>; order: string[]; css: Record<string, string> }>({ variants: {}, visible: new Set<string>(), order: [], css: {} });
  const overlayRef = useRef<HTMLDivElement>(null);
  const isReorderingRef = useRef(false);
  const [allowCrossContainer, setAllowCrossContainer] = useState(false);
  const [pendingInsertIndex, setPendingInsertIndex] = useState<number | null>(null);
  const [containerGridCols, setContainerGridCols] = useState<number>(0);
  const elementOrderRef = useRef<string[]>([]);
  useEffect(() => { elementOrderRef.current = elementOrder; }, [elementOrder]);
  const applyVisualReorderRef = useRef<(order: string[]) => void>(() => {});
  
  // Draggable panel states
  const [panelPositions, setPanelPositions] = useState({
    stylePicker: { x: 0, y: 0, width: 360, height: 550 },
    layersPanel: { x: 0, y: 0, width: 320, height: 600 },
    visualStylePanel: { x: 0, y: 60, width: 320, height: 700 },
    elementPicker: { x: 0, y: 0, width: 600, height: 600 },
    elementsPanel: { x: 0, y: 72, width: 420, height: 700 }
  });
  const [draggingPanel, setDraggingPanel] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizingPanel, setResizingPanel] = useState<string | null>(null);
  
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('blue.50', 'blue.900');
  const primaryColor = clubTheme.primary || '#0b5cff';
  const secondaryColor = clubTheme.secondary || '#ffd200';
  const selectedBg = primaryColor;

  const getAvailableVariants = useCallback((elementName: string) => {
    const variants = ELEMENT_VARIANTS[elementName] || [];
    if (pageType === 'homepage') {
      const allowed = SUPPORTED_HOME_VARIANTS[elementName];
      if (allowed && allowed.length > 0) {
        return variants.filter(variant => allowed.includes(variant.value));
      }
    }
    return variants;
  }, [pageType]);

  const getDefaultVariant = useCallback((elementName: string) => {
    const element = PREDEFINED_ELEMENTS.find(e => e.name === elementName);
    const candidate = element?.defaultVariant;
    const available = getAvailableVariants(elementName);
    if (available.length === 0) {
      return candidate || 'default';
    }
    if (candidate && available.some(v => v.value === candidate)) {
      return candidate;
    }
    return available[0].value;
  }, [getAvailableVariants]);

  const normalizeVariant = useCallback((elementName: string, variant?: string) => {
    const available = getAvailableVariants(elementName);
    if (available.length === 0) {
      return variant || getDefaultVariant(elementName);
    }
    if (variant && available.some(v => v.value === variant)) {
      return variant;
    }
    return getDefaultVariant(elementName);
  }, [getAvailableVariants, getDefaultVariant]);

  // Draggable panel handlers
  const handlePanelMouseDown = useCallback((panelName: string, e: React.MouseEvent) => {
    // Only allow dragging from header area
    const target = e.target as HTMLElement;
    if (!target.closest('.panel-header') && !target.classList.contains('panel-header')) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDraggingPanel(panelName);
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  }, []);

  const handlePanelMouseMove = useCallback((e: MouseEvent) => {
    if (!draggingPanel) return;
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    setPanelPositions(prev => ({
      ...prev,
      [draggingPanel]: {
        ...prev[draggingPanel as keyof typeof prev],
        x: Math.max(0, Math.min(newX, window.innerWidth - prev[draggingPanel as keyof typeof prev].width)),
        y: Math.max(0, Math.min(newY, window.innerHeight - prev[draggingPanel as keyof typeof prev].height))
      }
    }));
  }, [draggingPanel, dragOffset]);

  const handlePanelMouseUp = useCallback(() => {
    setDraggingPanel(null);
    setResizingPanel(null);
  }, []);

  // Panel dragging effect
  useEffect(() => {
    if (draggingPanel) {
      window.addEventListener('mousemove', handlePanelMouseMove);
      window.addEventListener('mouseup', handlePanelMouseUp);
      return () => {
        window.removeEventListener('mousemove', handlePanelMouseMove);
        window.removeEventListener('mouseup', handlePanelMouseUp);
      };
    }
  }, [draggingPanel, handlePanelMouseMove, handlePanelMouseUp]);

  // Resize handler
  const handleResizeStart = useCallback((panelName: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingPanel(panelName);
    setDragOffset({ x: e.clientX, y: e.clientY });
  }, []);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!resizingPanel) return;
    
    const deltaX = e.clientX - dragOffset.x;
    const deltaY = e.clientY - dragOffset.y;
    
    setPanelPositions(prev => {
      const panel = prev[resizingPanel as keyof typeof prev];
      return {
        ...prev,
        [resizingPanel]: {
          ...panel,
          width: Math.max(280, panel.width + deltaX),
          height: Math.max(300, panel.height + deltaY)
        }
      };
    });
    
    setDragOffset({ x: e.clientX, y: e.clientY });
  }, [resizingPanel, dragOffset]);

  useEffect(() => {
    if (resizingPanel) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handlePanelMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handlePanelMouseUp);
      };
    }
  }, [resizingPanel, handleResizeMove, handlePanelMouseUp]);

  // Auto-activate editing mode if URL parameter is present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('myuibrix') === 'edit') {
      setIsEditing(true);
      // Remove parameter from URL without reload
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      toast({
        title: 'MyUIbrix Editor',
        description: 'Režim úprav byl aktivován. Klikněte na elementy pro jejich úpravu.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toggle body class for edit mode so other parts can detect reliably
  useEffect(() => {
    try {
      if (isEditing) {
        document.body.classList.add('myuibrix-edit-mode');
      } else {
        document.body.classList.remove('myuibrix-edit-mode');
      }
    } catch {}
    return () => {
      try {
        document.body.classList.remove('myuibrix-edit-mode');
      } catch {}
    };
  }, [isEditing]);

  // Editor-only CSS: hide MyClub watermark and stabilize footer appearance in edit mode
  useEffect(() => {
    let styleEl: HTMLStyleElement | null = null;
    try {
      if (isEditing) {
        styleEl = document.createElement('style');
        styleEl.id = 'myuibrix-footer-editor-fixes';
        styleEl.textContent = `
          body.myuibrix-edit-mode [data-watermark="myclub"] { display: none !important; }
          body.myuibrix-edit-mode [data-element="footer"] { position: relative; z-index: 0; }
        `;
        document.head.appendChild(styleEl);
      }
    } catch {}
    return () => {
      try { const n = document.getElementById('myuibrix-footer-editor-fixes'); if (n) n.remove(); } catch {}
    };
  }, [isEditing]);

  // Toggle cross-container reorder mode class for global checks
  useEffect(() => {
    try {
      if (allowCrossContainer) {
        document.body.classList.add('myuibrix-cross-container-reorder');
      } else {
        document.body.classList.remove('myuibrix-cross-container-reorder');
      }
    } catch {}
    return () => {
      try { document.body.classList.remove('myuibrix-cross-container-reorder'); } catch {}
    };
  }, [allowCrossContainer]);

  // Auto-open Layers panel on the left by default when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setShowLayersPanel(false);
    }
  }, [isEditing]);

  // Detect grid columns on main container for grid insertion UI
  useEffect(() => {
    try {
      const el = safeDOM.querySelector('.container') as HTMLElement | null;
      if (!el) { setContainerGridCols(0); return; }
      const cs = window.getComputedStyle(el);
      if (cs.display !== 'grid') { setContainerGridCols(0); return; }
      const gtc = cs.gridTemplateColumns || '';
      let cols = 0;
      const m = gtc.match(/repeat\((\d+)/);
      if (m) cols = parseInt(m[1], 10);
      if (!cols) {
        // Fallback: naive split
        cols = gtc.split(' ').filter(Boolean).length || 2;
      }
      setContainerGridCols(cols);
    } catch {
      setContainerGridCols(0);
    }
  }, [isEditing, elementStyles]);

  // Auto-dismiss help hint after 5 seconds
  useEffect(() => {
    if (isEditing && showHelpHint) {
      const timer = setTimeout(() => {
        setShowHelpHint(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isEditing, showHelpHint]);

  // Load configurations
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const data = await getPageElementConfigs(pageType);
        const configsToUse = data.length > 0 ? data : DEFAULT_HOMEPAGE_ELEMENTS;
        const sanitizedConfigs = configsToUse.map(cfg => ({
          ...cfg,
          variant: normalizeVariant(cfg.element_name, cfg.variant)
        }));
        // Load saved styles and custom CSS from settings
        const cssByElement: Record<string, string> = {};
        const stylesByElement: Record<string, Record<string, any>> = {};
        sanitizedConfigs.forEach(cfg => {
          const css = String(((cfg.settings as any)?.customCSS) || ((cfg.settings as any)?.styles?.customCSS) || '');
          if (css) cssByElement[cfg.element_name] = css;
          const st = (cfg.settings && (cfg.settings as any).styles) || {};
          if (st && typeof st === 'object' && Object.keys(st).length > 0) {
            stylesByElement[cfg.element_name] = st as Record<string, any>;
          }
        });
        setConfigs(sanitizedConfigs);
        const changes: Record<string, string> = {};
        const visible = new Set<string>();
        const sorted = [...sanitizedConfigs].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        const order: string[] = [];
        sorted.forEach(cfg => {
          changes[cfg.element_name] = cfg.variant;
          if (cfg.visible !== false) {
            visible.add(cfg.element_name);
          }
          order.push(cfg.element_name);
        });
        
        setLocalChanges(changes);
        setVisibleElements(visible);
        setElementOrder(order);
        // Prime style state with saved styles + custom CSS
        if (Object.keys(stylesByElement).length > 0 || Object.keys(cssByElement).length > 0) {
          setElementStyles(prev => {
            const next = { ...prev } as Record<string, any>;
            Object.entries(stylesByElement).forEach(([name, st]) => {
              next[name] = { ...(next[name] || {}), ...st };
            });
            Object.entries(cssByElement).forEach(([name, css]) => {
              next[name] = { ...(next[name] || {}), customCSS: css };
            });
            return next;
          });
          // Inject saved custom CSS for preview (admin only)
          Object.entries(cssByElement).forEach(([name, css]) => {
            try {
              const styleId = `custom-css-${name}`;
              const existing = document.getElementById(styleId);
              if (existing) existing.remove();
              const style = document.createElement('style');
              style.id = styleId;
              const hasBlocks = /\{[^}]*\}|@media|@keyframes/.test(css);
              if (hasBlocks) {
                style.textContent = css;
              } else {
                const importantDecls = css
                  .split(';')
                  .map(s => s.trim())
                  .filter(Boolean)
                  .map(s => (/!important\s*$/.test(s) ? s : `${s} !important`))
                  .join(';\n            ');
                style.textContent = `\n          [data-element="${name}"] {\n            ${importantDecls};\n          }\n        `;
              }
              document.head.appendChild(style);
            } catch {}
          });
        }
        setBaseline({ variants: { ...changes }, visible: new Set<string>(visible), order: [...order], css: cssByElement });
        
        // If using defaults and no data exists, treat everything as unsaved
        if (data.length === 0) {
          setHasChanges(true);
          setBaseline({ variants: {}, visible: new Set<string>(), order: [], css: {} });
        }
      } catch (error) {
        console.error('Failed to load page element configs:', error);
        
        const fallbackConfigs = DEFAULT_HOMEPAGE_ELEMENTS.map(cfg => ({
          ...cfg,
          variant: normalizeVariant(cfg.element_name, cfg.variant)
        }));
        const changes: Record<string, string> = {};
        const visible = new Set<string>();
        const order: string[] = [];
        fallbackConfigs.forEach(cfg => {
          changes[cfg.element_name] = cfg.variant;
          if (cfg.visible !== false) {
            visible.add(cfg.element_name);
          }
          order.push(cfg.element_name);
        });
        setConfigs(fallbackConfigs);
        setLocalChanges(changes);
        setVisibleElements(visible);
        setElementOrder(order);
        setHasChanges(true);
        // Treat fallback like unsaved defaults so counter encourages save
        setBaseline({ variants: {}, visible: new Set<string>(), order: [], css: {} });
      }
    };

    loadConfigs();
  }, [pageType, normalizeVariant]);

  // Compute unsaved changes count by diffing against baseline
  const unsavedCount = useMemo(() => {
    try {
      const saved = baseline || { variants: {}, visible: new Set<string>(), order: [], css: {} } as any;
      const names = new Set<string>([
        ...Object.keys(localChanges || {}),
        ...Object.keys(saved.variants || {}),
        ...elementOrder,
        ...saved.order,
      ]);
      const changedElements = new Set<string>();
      names.forEach((name) => {
        const curVar = normalizeVariant(name, localChanges[name]);
        const savVar = normalizeVariant(name, saved.variants[name]);
        if ((curVar || '') !== (savVar || '')) changedElements.add(name);
        const curVis = visibleElements.has(name);
        const savVis = saved.visible.has(name);
        if (curVis !== savVis) changedElements.add(name);
        const curCSS = String((elementStyles[name]?.customCSS || '')).trim();
        const savCSS = String((saved.css?.[name] || '')).trim();
        if (curCSS !== savCSS) changedElements.add(name);
      });
      const orderEqual = (elementOrder.length === saved.order.length) && elementOrder.every((n, i) => n === saved.order[i]);
      return changedElements.size + (orderEqual ? 0 : 1);
    } catch {
      return 0;
    }
  }, [localChanges, visibleElements, elementOrder, baseline, normalizeVariant]);

  // Keep hasChanges in sync with computed counter
  useEffect(() => {
    setHasChanges(unsavedCount > 0);
  }, [unsavedCount]);

  const historyRef = useRef<Array<{ localChanges: Record<string, string>; visible: string[]; order: string[]; styles: Record<string, any> }>>([]);
  const historyIndexRef = useRef<number>(-1);
  const pushHistorySnapshot = useCallback(() => {
    try {
      const snap = {
        localChanges: { ...localChanges },
        visible: Array.from(visibleElements),
        order: [...elementOrder],
        styles: JSON.parse(JSON.stringify(elementStyles || {})),
      };
      const arr = historyRef.current.slice(0, historyIndexRef.current + 1);
      arr.push(snap);
      historyRef.current = arr;
      historyIndexRef.current = arr.length - 1;
    } catch {}
  }, [localChanges, visibleElements, elementOrder, elementStyles]);
  const canUndo = () => historyIndexRef.current > 0;
  const canRedo = () => historyIndexRef.current >= 0 && historyIndexRef.current < historyRef.current.length - 1;
  const applySnapshot = useCallback((idx: number) => {
    const snap = historyRef.current[idx];
    if (!snap) return;
    setLocalChanges(snap.localChanges);
    setVisibleElements(new Set(snap.visible));
    setElementOrder(snap.order);
    setElementStyles(snap.styles);
    try {
      if (isEditing) {
        requestAnimationFrame(() => {
          applyVisualReorderRef.current(snap.order);
          window.dispatchEvent(new CustomEvent('myuibrix-reorder', { detail: { order: snap.order, previewMode: true } }));
        });
      }
    } catch {}
  }, [isEditing]);
  const undo = useCallback(() => {
    if (!canUndo()) return;
    historyIndexRef.current -= 1;
    applySnapshot(historyIndexRef.current);
  }, [applySnapshot]);
  const redo = useCallback(() => {
    if (!canRedo()) return;
    historyIndexRef.current += 1;
    applySnapshot(historyIndexRef.current);
  }, [applySnapshot]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showElementPicker) setShowElementPicker(false);
        else if (showLayersPanel) setShowLayersPanel(false);
        else if (selectedElement) setSelectedElement(null);
        else handleExitEditing();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && hasChanges) {
        e.preventDefault();
        handleSave();
      }
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.code === 'KeyZ')) {
        e.preventDefault();
        undo();
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'z' || e.code === 'KeyZ')) {
        e.preventDefault();
        redo();
      }
      if (e.key === 'l' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        setShowLayersPanel(!showLayersPanel);
      }
      if (e.key === 'a' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        setShowElementPicker(true);
      }
      if (selectedElement) {
        if (e.key === 'ArrowUp' && !e.shiftKey) {
          e.preventDefault();
          handleMoveUp(selectedElement);
        }
        if (e.key === 'ArrowDown' && !e.shiftKey) {
          e.preventDefault();
          handleMoveDown(selectedElement);
        }
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          handleRemoveElement(selectedElement);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, selectedElement, showElementPicker, showLayersPanel, hasChanges, undo, redo]);

  useEffect(() => {
    if (!isEditing) {
      // Clean up overlays when exiting edit mode
      try {
        safeDOM.querySelectorAll('.elementor-overlay').forEach(el => {
          try { el.replaceWith(el.cloneNode(true)); } catch {}
          try { el.remove(); } catch {}
        });
      } catch {}
      setSelectedElement(null);
      return;
    }

    const addOverlay = (elementName: string) => {
      const selector = `[data-element="${elementName}"]`;
      const elements = safeDOM.querySelectorAll(selector);
      
      elements.forEach((element) => {
        const existing = element.querySelector('.elementor-overlay');
        if (existing) return;

        const overlay = document.createElement('div');
        overlay.className = 'elementor-overlay';
        overlay.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: auto;
          border: 2px dashed transparent;
          transition: all 0.2s;
          z-index: 9998;
          cursor: move;
          user-select: none;
          -webkit-user-select: none;
        `;

        const badge = document.createElement('div');
        badge.className = 'elementor-badge';
        badge.textContent = elementName.toUpperCase();
        badge.style.cssText = `
          position: absolute;
          top: -1px;
          left: -1px;
          background: ${primaryColor};
          color: white;
          padding: 4px 12px;
          font-size: 11px;
          font-weight: 700;
          border-radius: 0 0 6px 0;
          opacity: 0;
          transition: opacity 0.2s;
          pointer-events: none;
          z-index: 9999;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        `;

        // Action buttons (visible on hover)
        const actionsBar = document.createElement('div');
        actionsBar.className = 'elementor-actions';
        actionsBar.style.cssText = `
          position: absolute;
          top: 8px;
          right: 8px;
          display: flex;
          gap: 4px;
          opacity: 0;
          transition: opacity 0.2s;
          z-index: 10000;
          pointer-events: auto;
        `;

        // Edit button
        const editBtn = document.createElement('button');
        editBtn.innerHTML = '⚙️';
        editBtn.title = 'Upravit styl';
        editBtn.style.cssText = `
          background: ${primaryColor};
          color: white;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          transition: transform 0.2s;
        `;
        editBtn.onmouseover = () => editBtn.style.transform = 'scale(1.1)';
        editBtn.onmouseout = () => editBtn.style.transform = 'scale(1)';

        // Move up button
        const moveUpBtn = document.createElement('button');
        moveUpBtn.innerHTML = '⬆️';
        moveUpBtn.title = 'Přesunout nahoru';
        moveUpBtn.style.cssText = editBtn.style.cssText;
        moveUpBtn.onmouseover = () => moveUpBtn.style.transform = 'scale(1.1)';
        moveUpBtn.onmouseout = () => moveUpBtn.style.transform = 'scale(1)';

        // Move down button
        const moveDownBtn = document.createElement('button');
        moveDownBtn.innerHTML = '⬇️';
        moveDownBtn.title = 'Přesunout dolů';
        moveDownBtn.style.cssText = editBtn.style.cssText;
        moveDownBtn.onmouseover = () => moveDownBtn.style.transform = 'scale(1.1)';
        moveDownBtn.onmouseout = () => moveDownBtn.style.transform = 'scale(1)';

        // Delete button
        const deleteBtn = document.createElement('button');
        deleteBtn.innerHTML = '🗑️';
        deleteBtn.title = 'Odstranit element';
        deleteBtn.style.cssText = `
          background: #e53e3e;
          color: white;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          transition: transform 0.2s;
        `;
        deleteBtn.onmouseover = () => deleteBtn.style.transform = 'scale(1.1)';
        deleteBtn.onmouseout = () => deleteBtn.style.transform = 'scale(1)';

        // Add before button
        const addBeforeBtn = document.createElement('button');
        addBeforeBtn.innerHTML = '➕';
        addBeforeBtn.title = 'Přidat před';
        addBeforeBtn.style.cssText = editBtn.style.cssText;
        addBeforeBtn.onmouseover = () => addBeforeBtn.style.transform = 'scale(1.1)';
        addBeforeBtn.onmouseout = () => addBeforeBtn.style.transform = 'scale(1)';
        // Add after button
        const addAfterBtn = document.createElement('button');
        addAfterBtn.innerHTML = '➕';
        addAfterBtn.title = 'Přidat za';
        addAfterBtn.style.cssText = editBtn.style.cssText;
        addAfterBtn.onmouseover = () => addAfterBtn.style.transform = 'scale(1.1)';
        addAfterBtn.onmouseout = () => addAfterBtn.style.transform = 'scale(1)';

        // Grid column quick-insert buttons (add into specific grid column)
        let colWrap: HTMLDivElement | null = null;
        if (containerGridCols > 1) {
          colWrap = document.createElement('div');
          colWrap.className = 'elementor-col-picker';
          colWrap.style.cssText = `
            display: flex;
            gap: 4px;
          `;
          for (let c = 0; c < containerGridCols; c++) {
            const colBtn = document.createElement('button');
            colBtn.innerHTML = '➕';
            colBtn.title = `Přidat do sloupce ${c + 1}`;
            colBtn.style.cssText = editBtn.style.cssText;
            colBtn.onmouseover = () => (colBtn.style.transform = 'scale(1.1)');
            colBtn.onmouseout = () => (colBtn.style.transform = 'scale(1)');
            colBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              try {
                const cols = Math.max(1, containerGridCols || 1);
                const L = elementOrderRef.current.length;
                let countInCol = 0;
                for (let i = 0; i < L; i++) {
                  if (i % cols === c) countInCol++;
                }
                const targetIndex = c + countInCol * cols;
                setPendingInsertIndex(Math.min(targetIndex, elementOrderRef.current.length));
                setShowElementPicker(true);
              } catch {}
            });
            safeDOM.appendChild(colWrap!, colBtn);
          }
        }

        // Use safeDOM to build overlay structure
        safeDOM.appendChild(actionsBar, editBtn);
        safeDOM.appendChild(actionsBar, moveUpBtn);
        safeDOM.appendChild(actionsBar, moveDownBtn);
        safeDOM.appendChild(actionsBar, addBeforeBtn);
        safeDOM.appendChild(actionsBar, addAfterBtn);
        if (containerGridCols > 1 && colWrap) {
          safeDOM.appendChild(actionsBar, colWrap);
        }
        safeDOM.appendChild(actionsBar, deleteBtn);

        safeDOM.appendChild(overlay, badge);
        safeDOM.appendChild(overlay, actionsBar);
        
        const parentPos = window.getComputedStyle(element).position;
        if (parentPos === 'static') {
          (element as HTMLElement).style.position = 'relative';
        }

        // Add overlay using safeDOM to avoid React conflicts
        if (!safeDOM.appendChild(element, overlay)) {
          console.warn(`Failed to add overlay to element: ${elementName}`);
          return;
        }

        // Click to auto-select and open style panel
        overlay.addEventListener('click', (e) => {
          // Don't trigger if clicking on action buttons
          if ((e.target as HTMLElement).closest('.elementor-actions')) {
            return;
          }
          e.stopPropagation();
          const rect = element.getBoundingClientRect();
          setElementPosition({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
          setSelectedElement(elementName);
          setShowStylePanel(true);
        });

        // Hover effects
        overlay.addEventListener('mouseenter', () => {
          setHoveredElement(elementName);
          overlay.style.border = `2px dashed ${primaryColor}`;
          overlay.style.background = `${primaryColor}15`;
          badge.style.opacity = '1';
          actionsBar.style.opacity = '1';
        });

        overlay.addEventListener('mouseleave', () => {
          setHoveredElement(null);
          if (selectedElement !== elementName) {
            overlay.style.border = '2px dashed transparent';
            overlay.style.background = 'transparent';
            badge.style.opacity = '0';
            actionsBar.style.opacity = '0';
          }
        });

        // Button click handlers
        editBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const rect = element.getBoundingClientRect();
          setElementPosition({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          });
          setSelectedElement(elementName);
          setShowStylePanel(true);
        });

        moveUpBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          handleMoveUp(elementName);
        });

        moveDownBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          handleMoveDown(elementName);
        });

        deleteBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (confirm(`Opravdu chcete odstranit element "${elementName}"?`)) {
            handleRemoveElement(elementName);
          }
        });

        // Add before/after handlers
        addBeforeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          try {
            const idx = elementOrderRef.current.indexOf(elementName);
            if (idx >= 0) {
              setPendingInsertIndex(idx);
              setShowElementPicker(true);
            }
          } catch {}
        });
        addAfterBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          try {
            const idx = elementOrderRef.current.indexOf(elementName);
            if (idx >= 0) {
              setPendingInsertIndex(idx + 1);
              setShowElementPicker(true);
            }
          } catch {}
        });

        // per-column insert handled by elementor-col-picker buttons above

        // Make overlay draggable
        overlay.draggable = true;
        overlay.addEventListener('dragstart', (e) => {
          e.stopPropagation();
          try { (e as DragEvent).dataTransfer?.setData('text/plain', elementName); } catch {}
          setDraggedElement(elementName);
          overlay.style.opacity = '0.5';
        });

        overlay.addEventListener('dragend', (e) => {
          e.stopPropagation();
          overlay.style.opacity = '1';
          setDraggedElement(null);
        });

        overlay.addEventListener('dragover', (e) => {
          e.preventDefault();
          e.stopPropagation();
          try { (e as DragEvent).dataTransfer!.dropEffect = 'move'; } catch {}
          if (draggedElement && draggedElement !== elementName) {
            overlay.style.border = `3px solid ${secondaryColor}`;
            setDragOverElement(elementName);
          }
        });

        overlay.addEventListener('dragleave', (e) => {
          e.stopPropagation();
          if (selectedElement !== elementName) {
            overlay.style.border = `2px dashed ${primaryColor}`;
          }
          setDragOverElement(null);
        });

        overlay.addEventListener('drop', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (draggedElement && draggedElement !== elementName) {
            // Reorder elements
            const newOrder = [...elementOrderRef.current];
            const draggedIndex = newOrder.indexOf(draggedElement as string);
            const targetIndex = newOrder.indexOf(elementName);
            
            if (draggedIndex !== -1 && targetIndex !== -1) {
              newOrder.splice(draggedIndex, 1);
              newOrder.splice(targetIndex, 0, draggedElement as string);
              setElementOrder(newOrder);
              setHasChanges(true);
              applyVisualReorderRef.current(newOrder);
            }
          }
          overlay.style.border = `2px dashed ${primaryColor}`;
          setDragOverElement(null);
        });
      });
    };
        
        // Add overlays for all present [data-element] nodes in DOM (dynamic)
        try {
          const nodes = Array.from(safeDOM.querySelectorAll('[data-element]')) as HTMLElement[];
          const names = Array.from(new Set(nodes
            .map(n => n.getAttribute('data-element'))
            .filter((v): v is string => !!v && v !== 'container')
          ));
          names.forEach(name => addOverlay(name));
        } catch {
          // fallback to implemented list if needed
          const implementedElements = pageType === 'homepage' ? HOMEPAGE_IMPLEMENTED_ELEMENTS : Object.keys(ELEMENT_VARIANTS);
          implementedElements.forEach((elementName) => {
            if (ELEMENT_VARIANTS[elementName]) {
              addOverlay(elementName);
            }
          });
        }
        const handleEscape = (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            setSelectedElement(null);
          }
        };
    document.addEventListener('keydown', handleEscape);

    return () => {
      // Cleanup: Remove all overlays and event listeners - with safe DOM
      try {
        const overlays = safeDOM.querySelectorAll('.elementor-overlay');
        overlays.forEach(el => {
          try {
            // Remove event listeners by cloning
            const clone = el.cloneNode(false) as Element;
            const parent = el.parentElement;
            if (parent && safeDOM.replaceChild(parent, clone, el)) {
              safeDOM.removeChild(parent, clone);
            }
          } catch (e) {
            console.warn('Failed to cleanup overlay:', e);
          }
        });
      } catch (e) {
        console.error('Error during cleanup:', e);
      }
      
      try {
        document.removeEventListener('keydown', handleEscape);
      } catch (e) {
        console.warn('Failed to remove event listener:', e);
      }
      
      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [isEditing, selectedElement, pageType, elementOrder, visibleElements]);

  // Update selected element overlay styling
  useEffect(() => {
    safeDOM.querySelectorAll('.elementor-overlay').forEach((overlay) => {
      const parent = overlay.parentElement;
      const elementName = parent?.getAttribute('data-element');
      
      if (elementName === selectedElement) {
        (overlay as HTMLElement).style.border = `3px solid ${primaryColor}`;
        (overlay as HTMLElement).style.background = `${primaryColor}1A`;
        const badge = overlay.querySelector('.elementor-badge') as HTMLElement;
        if (badge) {
          badge.style.opacity = '1';
          badge.style.background = primaryColor;
        }
      } else if (elementName === hoveredElement) {
        (overlay as HTMLElement).style.border = `2px dashed ${primaryColor}`;
        (overlay as HTMLElement).style.background = `${primaryColor}15`;
        const badge = overlay.querySelector('.elementor-badge') as HTMLElement;
        if (badge) badge.style.opacity = '1';
      } else {
        (overlay as HTMLElement).style.border = '2px dashed transparent';
        (overlay as HTMLElement).style.background = 'transparent';
        const badge = overlay.querySelector('.elementor-badge') as HTMLElement;
        if (badge) badge.style.opacity = '0';
      }
    });
  }, [selectedElement, hoveredElement]);

  const handleVariantChange = useCallback((elementName: string, variant: string) => {
    // Prevent crashes by checking if variant exists
    const variants = getAvailableVariants(elementName);
    if (!variants || variants.length === 0) {
      console.warn(`Invalid variant "${variant}" for element "${elementName}"`);
      return;
    }
    const safeVariant = normalizeVariant(elementName, variant);

    // Helper function to apply the variant change
    const applyChange = () => {
      try {
        pushHistorySnapshot();
        const newChanges = { ...localChanges, [elementName]: safeVariant };
        setLocalChanges(newChanges);
        setHasChanges(true);
        
        // Update configs array to reflect the change
        setConfigs(prevConfigs => {
          const configIndex = prevConfigs.findIndex(c => c.element_name === elementName);
          if (configIndex !== -1) {
            const updated = [...prevConfigs];
            updated[configIndex] = { ...updated[configIndex], variant: safeVariant };
            return updated;
          }
          return prevConfigs;
        });
        
        // Live preview ONLY during editing (not visible in production)
        // Use requestAnimationFrame to avoid DOM conflicts
        if (isEditing) {
          requestAnimationFrame(() => {
            window.dispatchEvent(new CustomEvent('myuibrix-change', {
              detail: { elementName, variant: safeVariant, visible: visibleElements.has(elementName), previewMode: true }
            }));
          });
        }
      } catch (error) {
        console.error('Error applying variant change:', error);
        toast({
          title: 'Chyba při aplikaci stylu',
          description: 'Styl se nepodařilo aplikovat. Zkuste to prosím znovu.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    };
    
    // Wait if we're currently reordering to avoid DOM conflicts
    if (isReorderingRef.current) {
      // Wait for reordering to complete, then apply change
      const checkAndApply = () => {
        if (!isReorderingRef.current) {
          applyChange();
        } else {
          setTimeout(checkAndApply, 50);
        }
      };
      setTimeout(checkAndApply, 50);
      return;
    }
    
    applyChange();
  }, [localChanges, visibleElements, isEditing, toast, getAvailableVariants, normalizeVariant]);

  // Apply visual reordering with optional cross-container moves
  const applyVisualReorder = useCallback((order: string[]) => {
    if (isReorderingRef.current) return;
    isReorderingRef.current = true;

    requestAnimationFrame(() => {
      try {
        const cross = allowCrossContainer || (typeof document !== 'undefined' && document.body?.classList?.contains('myuibrix-cross-container-reorder'));
        if (cross) {
          // Prefer the in-page content wrapper as the canonical parent
          const primary = (safeDOM.querySelector('.container') as HTMLElement) ||
                          (safeDOM.querySelector('.myuibrix-viewport-wrapper') as HTMLElement) ||
                          (safeDOM.querySelector('main') as HTMLElement) || null;

          if (primary) {
            // Move any element not under the primary into it
            order.forEach((name) => {
              const el = safeDOM.querySelector(`[data-element="${name}"]`) as HTMLElement | null;
              if (el && el.parentElement !== primary) {
                safeDOM.appendChild(primary, el);
              }
            });
            // Append in requested order
            order.forEach((name) => {
              const el = safeDOM.querySelector(`[data-element="${name}"]`) as HTMLElement | null;
              if (el) {
                try { el.style.order = ''; } catch {}
                safeDOM.appendChild(primary, el);
              }
            });
          }
        } else {
          // Reorder only within each element's existing parent
          const parentMap = new Map<HTMLElement, HTMLElement[]>();
          order.forEach((name) => {
            const el = safeDOM.querySelector(`[data-element="${name}"]`) as HTMLElement | null;
            if (!el || !el.parentElement) return;
            const parent = el.parentElement as HTMLElement;
            if (!parentMap.has(parent)) parentMap.set(parent, []);
            parentMap.get(parent)!.push(el);
          });
          parentMap.forEach((els, parent) => {
            els.forEach((el) => { try { el.style.order = ''; } catch {} });
            order.forEach((name) => {
              const el = safeDOM.querySelector(`[data-element="${name}"]`) as HTMLElement | null;
              if (el && el.parentElement === parent) {
                safeDOM.appendChild(parent, el);
              }
            });
          });
        }

        // Notify listeners (HomePage hook) about the new order
        window.dispatchEvent(new CustomEvent('myuibrix-reorder', {
          detail: { order, previewMode: true }
        }));

        setTimeout(() => { isReorderingRef.current = false; }, 50);
      } catch (error) {
        console.error('Error during DOM reordering:', error);
        isReorderingRef.current = false;
      }
    });
  }, [allowCrossContainer]);
  applyVisualReorderRef.current = applyVisualReorder;

  // Debounce style changes to prevent lag
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const handleStyleChange = useCallback((elementName: string, styles: Record<string, any>) => {
    setElementStyles(prev => ({
      ...prev,
      [elementName]: styles
    }));
    setHasChanges(true);
    
    // Debounce the dispatch to reduce event flooding
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      // Live preview ONLY during editing
      if (isEditing) {
        window.dispatchEvent(new CustomEvent('myuibrix-style-change', {
          detail: { elementName, styles, previewMode: true }
        }));
      }
    }, 100); // 100ms debounce
  }, [isEditing]);

  // Helper: compute insert index for a given grid column (append at end of that column)
  const computeInsertIndexForColumn = useCallback((col: number) => {
    const cols = Math.max(1, containerGridCols || 1);
    const L = elementOrderRef.current.length;
    let countInCol = 0;
    for (let i = 0; i < L; i++) {
      if (i % cols === col) countInCol++;
    }
    return col + countInCol * cols;
  }, [containerGridCols]);

  // Add element from picker and make it visible + ordered in preview
  const handleAddElement = useCallback((elementName: string, insertAt?: number) => {
    pushHistorySnapshot();
    const element = PREDEFINED_ELEMENTS.find(e => e.name === elementName);
    const existingVariant = localChanges[elementName];
    const defaultVariant = normalizeVariant(elementName, element?.defaultVariant || 'default');
    const variantToUse = normalizeVariant(elementName, existingVariant || defaultVariant);
    if (!localChanges[elementName]) {
      setLocalChanges(prev => ({ ...prev, [elementName]: variantToUse }));
    }
    // Mark as visible in editor state
    setVisibleElements(prev => {
      const next = new Set(prev);
      next.add(elementName);
      return next;
    });

    // Ensure config entry exists and is visible
    setConfigs(prev => {
      const index = prev.findIndex(cfg => cfg.element_name === elementName);
      if (index !== -1) {
        const updated = [...prev];
        updated[index] = { ...updated[index], variant: variantToUse, visible: true };
        return updated;
      }
      return [
        ...prev,
        {
          page_type: pageType,
          element_name: elementName,
          variant: variantToUse,
          visible: true,
          display_order: prev.length,
        }
      ];
    });

    // Close picker UI
    setHasChanges(true);
    setShowElementPicker(false);
    setSearchQuery('');
    setSelectedCategory('all');

    // Add into ordering at desired position and apply reordering
    setElementOrder(prev => {
      const targetIndex = (typeof insertAt === 'number') ? insertAt : (pendingInsertIndex != null ? pendingInsertIndex : undefined);
      if (prev.includes(elementName)) {
        try {
          toast({
            title: 'Duplicitní element',
            description: 'Tento element již na stránce existuje. Přesouvám existující na zvolené místo.',
            status: 'warning',
            duration: 2500,
            isClosable: true,
          });
        } catch {}
        const existingIdx = prev.indexOf(elementName);
        if (typeof targetIndex === 'number' && targetIndex !== existingIdx) {
          const reordered = [...prev];
          reordered.splice(existingIdx, 1);
          reordered.splice(Math.min(targetIndex, reordered.length), 0, elementName);
          if (isEditing) {
            requestAnimationFrame(() => {
              applyVisualReorder(reordered);
              window.dispatchEvent(new CustomEvent('myuibrix-reorder', { detail: { order: reordered, previewMode: true } }));
            });
          }
          return reordered;
        }
        if (isEditing) {
          requestAnimationFrame(() => {
            applyVisualReorder(prev);
            window.dispatchEvent(new CustomEvent('myuibrix-reorder', { detail: { order: prev, previewMode: true } }));
          });
        }
        return prev;
      }
      const newOrder = [...prev];
      if (typeof targetIndex === 'number') {
        newOrder.splice(Math.min(targetIndex, newOrder.length), 0, elementName);
      } else {
        newOrder.push(elementName);
      }
      if (isEditing) {
        requestAnimationFrame(() => {
          applyVisualReorder(newOrder);
          window.dispatchEvent(new CustomEvent('myuibrix-reorder', {
            detail: { order: newOrder, previewMode: true }
          }));
        });
      }
      // Ensure element is visible in DOM for editing
      setTimeout(() => {
        try {
          const el = safeDOM.querySelector(`[data-element="${elementName}"]`);
          if (el) {
            (el as HTMLElement).style.display = '';
          }
        } catch {}
      }, 0);
      return newOrder;
    });

    // Notify preview consumers to render element
    if (isEditing) {
      window.dispatchEvent(new CustomEvent('myuibrix-change', {
        detail: { elementName, variant: variantToUse, visible: true, previewMode: true }
      }));
    }
    setPendingInsertIndex(null);
    // Auto-select and open style panel for the newly added element
    try {
      setSelectedElement(elementName);
      setTimeout(() => {
        const el = safeDOM.querySelector(`[data-element="${elementName}"]`) as HTMLElement | null;
        if (el) {
          const rect = el.getBoundingClientRect();
          setElementPosition({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
          setShowStylePanel(true);
        }
      }, 0);
    } catch {}
  }, [localChanges, isEditing, normalizeVariant, pageType, applyVisualReorder, pendingInsertIndex]);

  const handleRemoveElement = useCallback((elementName: string) => {
    pushHistorySnapshot();
    // Update state - React will handle DOM removal
    const newVisible = new Set(visibleElements);
    newVisible.delete(elementName);
    setVisibleElements(newVisible);
    setHasChanges(true);
    setSelectedElement(null);
    
    // Hide the element via React state event
    if (isEditing) {
      window.dispatchEvent(new CustomEvent('myuibrix-change', {
        detail: { elementName, variant: localChanges[elementName], visible: false, previewMode: true }
      }));
    }
    
    // Force React to re-render by triggering a state update
    setTimeout(() => {
      const element = safeDOM.querySelector(`[data-element="${elementName}"]`);
      if (element) {
        (element as HTMLElement).style.display = 'none';
      }
    }, 0);
  }, [visibleElements, localChanges, isEditing]);

  

  const handleMoveUp = useCallback((elementName: string) => {
    pushHistorySnapshot();
    const currentIndex = elementOrder.indexOf(elementName);
    if (currentIndex <= 0) return;
    
    const newOrder = [...elementOrder];
    [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];
    setElementOrder(newOrder);
    setHasChanges(true);
    
    // Trigger reorder event and apply visual reordering
    if (isEditing) {
      applyVisualReorder(newOrder);
      window.dispatchEvent(new CustomEvent('myuibrix-reorder', {
        detail: { order: newOrder, previewMode: true }
      }));
    }
  }, [elementOrder, isEditing, applyVisualReorder]);

  const handleMoveDown = useCallback((elementName: string) => {
    pushHistorySnapshot();
    const currentIndex = elementOrder.indexOf(elementName);
    if (currentIndex < 0 || currentIndex >= elementOrder.length - 1) return;
    
    const newOrder = [...elementOrder];
    [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
    setElementOrder(newOrder);
    setHasChanges(true);
    
    // Trigger reorder event and apply visual reordering
    if (isEditing) {
      applyVisualReorder(newOrder);
      window.dispatchEvent(new CustomEvent('myuibrix-reorder', {
        detail: { order: newOrder, previewMode: true }
      }));
    }
  }, [elementOrder, isEditing, applyVisualReorder]);


  // Drag and drop handlers with improved state management
  const handleDragStart = useCallback((elementName: string, e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', elementName);
    setDraggedElement(elementName);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, elementName: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedElement !== elementName) {
      setDragOverElement(elementName);
    }
  }, [draggedElement]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear if we're leaving to a non-child element
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !(e.currentTarget as HTMLElement).contains(relatedTarget)) {
      setDragOverElement(null);
    }
  }, []);
  
  const handleDragEnd = useCallback(() => {
    setDraggedElement(null);
    setDragOverElement(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetElementName: string) => {
    e.preventDefault();
    
    if (!draggedElement || draggedElement === targetElementName) {
      setDraggedElement(null);
      setDragOverElement(null);
      return;
    }
    
    const newOrder = [...elementOrder];
    const draggedIndex = newOrder.indexOf(draggedElement);
    const targetIndex = newOrder.indexOf(targetElementName);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedElement(null);
      setDragOverElement(null);
      return;
    }
    
    // Remove dragged element and insert at target position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedElement);
    
    setElementOrder(newOrder);
    setHasChanges(true);
    setDraggedElement(null);
    setDragOverElement(null);
    
    // Apply visual reordering
    if (isEditing) {
      applyVisualReorder(newOrder);
      window.dispatchEvent(new CustomEvent('myuibrix-reorder', {
        detail: { order: newOrder, previewMode: true }
      }));
    }
  }, [draggedElement, elementOrder, isEditing, applyVisualReorder]);

  // Start with a blank layout: hide all elements and clear order
  const handleStartBlank = useCallback(() => {
    try {
      if (!confirm('Začít s prázdným rozložením? Všechny sekce (kromě hlavičky a patičky) budou skryté.')) {
        return;
      }
    } catch {}

    const previouslyVisible = Array.from(visibleElements);
    const keep = new Set(['header', 'footer']);
    const toHide = previouslyVisible.filter(n => !keep.has(n));
    const newVisible = new Set<string>();
    previouslyVisible.forEach(n => { if (keep.has(n)) newVisible.add(n); });
    setVisibleElements(newVisible);
    setElementOrder(prev => prev.filter(n => keep.has(n)));
    setHasChanges(true);

    if (isEditing) {
      toHide.forEach((elementName) => {
        try {
          window.dispatchEvent(new CustomEvent('myuibrix-change', {
            detail: { elementName, variant: localChanges[elementName], visible: false, previewMode: true }
          }));
          const el = safeDOM.querySelector(`[data-element="${elementName}"]`);
          if (el) {
            (el as HTMLElement).style.display = 'none';
          }
        } catch {}
      });
      requestAnimationFrame(() => {
        const order = Array.from(newVisible).filter(n => keep.has(n));
        applyVisualReorder(order);
        window.dispatchEvent(new CustomEvent('myuibrix-reorder', {
          detail: { order, previewMode: true }
        }));
      });
    }

    try {
      toast({
        title: 'Prázdné rozložení',
        description: 'Všechny sekce byly skryty. Můžete začít přidávat prvky.',
        status: 'info',
        duration: 2500,
        isClosable: true,
      });
    } catch {}

    setShowElementPicker(true);
  }, [visibleElements, isEditing, localChanges, applyVisualReorder, toast]);

  const handleSave = async () => {
    try {
      // Persist ALL elements that have order, styles, or variant/visibility changes
      const knownOrder = [...elementOrder];
      const extraNames = Array.from(new Set([
        ...Object.keys(elementStyles || {}),
        ...Object.keys(localChanges || {}),
        ...Array.from(visibleElements || new Set<string>())
      ].filter(Boolean))).filter(name => !knownOrder.includes(name));

      const allNames = [...knownOrder, ...extraNames];

      const configsToSave: PageElementConfig[] = allNames.map((elementName, idx) => ({
        page_type: pageType,
        element_name: elementName,
        variant: localChanges[elementName] || 'default',
        visible: visibleElements.has(elementName) || elementName === 'style-pack' || elementName === 'container',
        display_order: knownOrder.includes(elementName) ? knownOrder.indexOf(elementName) : idx,
        settings: {
          ...(configs.find(c => c.element_name === elementName)?.settings || {}),
          // Persist full styles object and custom CSS
          styles: (elementStyles[elementName] || (configs.find(c => c.element_name === elementName)?.settings as any)?.styles || {}),
          customCSS: (elementStyles[elementName]?.customCSS ?? (configs.find(c => c.element_name === elementName)?.settings as any)?.customCSS ?? ''),
        },
      }));

      // Try admin batch first (works for admins in dev/prod)
      let saved = false;
      try {
        await batchUpdatePageElementConfigs(configsToSave);
        saved = true;
      } catch (err: any) {
        // Fallback for editors (403/401): use editor preview apply endpoint
        const status = err?.response?.status || err?.status;
        if (status !== 401 && status !== 403) throw err;
        const sessionId = `${pageType}-autosave`;
        const elements = configsToSave.map(cfg => ({
          element_name: cfg.element_name,
          variant: cfg.variant,
          visible: Boolean(cfg.visible),
          display_order: Number(cfg.display_order || 0),
          custom_styles: ((cfg.settings as any)?.styles) || {},
        }));
        await api.post(`/editor/preview/${encodeURIComponent(sessionId)}/apply`, {
          page_type: pageType,
          elements,
        });
        saved = true;
      }

      if (!saved) return;
      
      toast({
        title: 'Změny úspěšně uloženy!',
        description: 'Změny jsou nyní viditelné na produkci.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Update baseline to current state so counter resets immediately
      setBaseline({
        variants: { ...localChanges },
        visible: new Set<string>(visibleElements),
        order: [...elementOrder],
        css: Object.fromEntries(Object.entries(elementStyles).map(([k, v]) => [k, String((v as any)?.customCSS || '')])),
      });
      setHasChanges(false);
      
      // Reload the page to apply changes in production view
      // This ensures saved changes are visible to all visitors
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast({
        title: 'Nepodařilo se uložit změny',
        description: 'Zkuste to prosím znovu nebo kontaktujte podporu.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Memoize current variants to avoid recalculation
  const currentVariants = useMemo(() => 
    selectedElement ? getAvailableVariants(selectedElement) : [],
    [selectedElement, getAvailableVariants]
  );

  const currentVariant = useMemo(() => 
    selectedElement ? normalizeVariant(selectedElement, localChanges[selectedElement]) : null,
    [selectedElement, localChanges, normalizeVariant]
  );

  const handleExitEditing = useCallback(() => {
    try { document.body.classList.remove('myuibrix-edit-mode'); } catch {}
    setIsEditing(false);
    setTimeout(() => {
      try { window.location.reload(); } catch {}
    }, 120);
  }, []);

  // Calculate viewport width - USE REAL DEVICE WIDTHS WITHOUT SCALING
  const getViewportConfig = useCallback(() => ({
    width: '100%',
    label: 'Desktop (100%)'
  }), []);

  const viewportConfig = useMemo(() => getViewportConfig(), [getViewportConfig]);

  // Apply viewport width changes with smooth transitions - REAL WIDTH CONSTRAINTS
  useEffect(() => {
    if (!isEditing) return;
    
    const wrapper = safeDOM.querySelector('.myuibrix-viewport-wrapper') as HTMLElement;
    if (!wrapper) return;
    
    // Apply actual width constraints without scaling for real responsive behavior
    wrapper.style.width = '100%';
    if (viewport === 'mobile') {
      wrapper.style.maxWidth = '420px';
    } else if (viewport === 'tablet') {
      wrapper.style.maxWidth = '820px';
    } else {
      wrapper.style.maxWidth = '100%';
    }
    wrapper.style.transition = 'all 0.3s ease';
    wrapper.style.margin = '0 auto';
    wrapper.style.transform = 'none';
    wrapper.style.transformOrigin = '';
    
    // Add visual indicator for non-desktop viewports only
    if (viewport !== 'desktop') {
      wrapper.style.border = `3px solid ${primaryColor}`;
      wrapper.style.boxShadow = `0 0 0 9999px rgba(0,0,0,0.25), 0 8px 32px rgba(0,0,0,0.2)`;
      wrapper.style.marginTop = '20px';
      wrapper.style.marginBottom = '20px';
      wrapper.style.minHeight = 'calc(100vh - 100px)';
    } else {
      wrapper.style.border = 'none';
      wrapper.style.boxShadow = 'none';
      wrapper.style.marginTop = '0';
      wrapper.style.marginBottom = '0';
      wrapper.style.minHeight = '100vh';
    }
    
    // Show toast notification when changing viewport
    const label = viewport === 'mobile' ? 'Mobile' : viewport === 'tablet' ? 'Tablet' : 'Desktop';
    const desc = viewport === 'mobile' ? 'Šířka ~420px' : viewport === 'tablet' ? 'Šířka ~820px' : 'Zobrazení na plnou šířku (100%)';
    toast({
      title: `Viewport: ${label}`,
      description: desc,
      status: 'info',
      duration: 1500,
      isClosable: true,
      position: 'bottom-right',
    });
  }, [isEditing, viewport, primaryColor, toast, viewportConfig]);

  // Prevent all clicks on page content during edit mode
  useEffect(() => {
    if (!isEditing) return;
    
    const preventPageInteractions = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Allow clicks on MyUIbrix UI elements (panels, buttons, overlays)
      if (
        target.closest('.myuibrix-panel') ||
        target.closest('.myuibrix-toolbar') ||
        target.closest('.elementor-overlay') ||
        target.closest('[role="dialog"]') ||
        target.closest('.chakra-portal') ||
        target.closest('.chakra-modal') ||
        target.classList.contains('elementor-overlay')
      ) {
        return; // Allow MyUIbrix interactions
      }
      
      // Prevent all other interactions on the page content
      const wrapper = safeDOM.querySelector('.myuibrix-viewport-wrapper');
      if (wrapper && wrapper.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };
    
    // Capture phase to ensure we intercept before any handlers
    document.addEventListener('click', preventPageInteractions, true);
    document.addEventListener('mousedown', preventPageInteractions, true);
    
    return () => {
      document.removeEventListener('click', preventPageInteractions, true);
      document.removeEventListener('mousedown', preventPageInteractions, true);
    };
  }, [isEditing]);

  // Add padding to body when editor is active and apply viewport width
  useEffect(() => {
    if (isEditing) {
      document.body.style.paddingTop = '60px';
      document.body.style.backgroundColor = '#e2e8f0';
      document.body.style.userSelect = 'none'; // Prevent text selection during editing
      
      const wrapperEl = safeDOM.querySelector('.container') as HTMLElement | null;
      if (wrapperEl) {
        try { wrapperEl.classList.add('myuibrix-viewport-wrapper'); } catch {}
        try { wrapperEl.setAttribute('data-myuibrix-wrapped', '1'); } catch {}
        try { wrapperEl.style.width = '100%'; } catch {}
        try { wrapperEl.style.maxWidth = '100%'; } catch {}
        try { wrapperEl.style.transition = 'all 0.3s ease'; } catch {}
        try { wrapperEl.style.margin = '0 auto'; } catch {}
        try { wrapperEl.style.transform = 'none'; } catch {}
        try { wrapperEl.style.transformOrigin = ''; } catch {}
      }
    } else {
      document.body.style.paddingTop = '0';
      document.body.style.backgroundColor = '';
      document.body.style.userSelect = '';
      
      const wrapperEl = safeDOM.querySelector('.myuibrix-viewport-wrapper') as HTMLElement | null;
      if (wrapperEl) {
        try { wrapperEl.classList.remove('myuibrix-viewport-wrapper'); } catch {}
        try { wrapperEl.removeAttribute('data-myuibrix-wrapped'); } catch {}
        try { wrapperEl.style.width = ''; } catch {}
        try { wrapperEl.style.maxWidth = ''; } catch {}
        try { wrapperEl.style.transition = ''; } catch {}
        try { wrapperEl.style.margin = ''; } catch {}
        try { wrapperEl.style.transform = ''; } catch {}
        try { wrapperEl.style.transformOrigin = ''; } catch {}
      }
    }
    return () => {
      document.body.style.paddingTop = '0';
      document.body.style.backgroundColor = '';
      document.body.style.userSelect = '';
      const wrapperEl = safeDOM.querySelector('.myuibrix-viewport-wrapper') as HTMLElement | null;
      if (wrapperEl) {
        try { wrapperEl.classList.remove('myuibrix-viewport-wrapper'); } catch {}
        try { wrapperEl.removeAttribute('data-myuibrix-wrapped'); } catch {}
        try { wrapperEl.style.width = ''; } catch {}
        try { wrapperEl.style.maxWidth = ''; } catch {}
        try { wrapperEl.style.transition = ''; } catch {}
        try { wrapperEl.style.margin = ''; } catch {}
        try { wrapperEl.style.transform = ''; } catch {}
        try { wrapperEl.style.transformOrigin = ''; } catch {}
      }
    };
  }, [isEditing]);

  // Early return if not admin (after all hooks)
  if (!isAdmin) return null;

  return (
    <>
      {/* Edit Mode - Active */}
      {isEditing && (
        <>
          {/* Top Control Bar - Actions */}
          <Box
            className="myuibrix-toolbar"
            position="fixed"
            top={0}
            left={0}
            right={0}
            bg="rgba(17, 25, 40, 0.55)"
            color="white"
            p={3}
            zIndex={10000}
            boxShadow="0 10px 30px rgba(0,0,0,0.2)"
            backdropFilter="saturate(180%) blur(14px)"
            borderBottom="1px solid rgba(255,255,255,0.12)"
            fontFamily="var(--chakra-fonts-body)"
          >
            <Flex align="center" justify="space-between" maxW="100%">
              {/* Left: Elementor Logo */}
              <HStack spacing={3}>
                <Box 
                  bgGradient={`linear(135deg, ${secondaryColor}, ${secondaryColor}dd)`}
                  px={4} 
                  py={2} 
                  borderRadius="xl" 
                  color={clubTheme.textOnSecondary || 'black'}
                  boxShadow="0 4px 12px rgba(0,0,0,0.2)"
                  border="2px solid rgba(255,255,255,0.2)"
                  transition="all 0.3s"
                  _hover={{ transform: 'translateY(-1px)', boxShadow: '0 6px 16px rgba(0,0,0,0.25)' }}
                >
                  <HStack spacing={2} align="center">
                    <Icon as={FaFutbol} color="white" boxSize={5} />
                    <Text fontWeight="800" fontSize="md" color="white" letterSpacing="tight">
                      MyUIbrix
                    </Text>
                  </HStack>
                </Box>
                <Badge
                  bg="orange.500"
                  color="white"
                  fontSize="xs"
                  px={3}
                  py={1.5}
                  borderRadius="full"
                  fontWeight="700"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  boxShadow="0 2px 8px rgba(237, 137, 54, 0.4)"
                  border="1px solid rgba(255,255,255,0.3)"
                  sx={{
                    '@keyframes pulseBadge': {
                      '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                      '50%': { transform: 'scale(1.05)', opacity: 0.9 }
                    },
                    animation: 'pulseBadge 2s infinite'
                  }}
                >
                  <Icon as={FiLock} boxSize={3} />
                  REŽIM NÁHLEDU
                </Badge>
                <Text fontSize="sm" fontWeight="600" color="whiteAlpha.800">Změny vidíte pouze vy</Text>
              </HStack>

              <Text fontSize="sm" fontWeight="700" color="whiteAlpha.900">
                Náhled: Desktop (100%)
              </Text>

              {/* Right: Actions */}
              <HStack spacing={2}>
                {/* Viewport toggles */}
                <Tooltip label="Mobilní náhled">
                  <IconButton
                    aria-label="Mobile viewport"
                    icon={<FiSmartphone />}
                    size="sm"
                    variant={viewport === 'mobile' ? 'solid' : 'ghost'}
                    colorScheme={viewport === 'mobile' ? 'blue' : 'whiteAlpha'}
                    onClick={() => setViewport('mobile')}
                  />
                </Tooltip>
                <Tooltip label="Tablet náhled">
                  <IconButton
                    aria-label="Tablet viewport"
                    icon={<FiTablet />}
                    size="sm"
                    variant={viewport === 'tablet' ? 'solid' : 'ghost'}
                    colorScheme={viewport === 'tablet' ? 'blue' : 'whiteAlpha'}
                    onClick={() => setViewport('tablet')}
                  />
                </Tooltip>
                <Tooltip label="Desktop náhled">
                  <IconButton
                    aria-label="Desktop viewport"
                    icon={<FiMonitor />}
                    size="sm"
                    variant={viewport === 'desktop' ? 'solid' : 'ghost'}
                    colorScheme={viewport === 'desktop' ? 'blue' : 'whiteAlpha'}
                    onClick={() => setViewport('desktop')}
                  />
                </Tooltip>
                <Divider orientation="vertical" borderColor="whiteAlpha.400" />
                {/* Undo/Redo */}
                <Tooltip label="Zpět (Ctrl+Z)">
                  <IconButton
                    aria-label="Undo"
                    icon={<FaUndo />}
                    size="sm"
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    onClick={undo}
                    isDisabled={!canUndo()}
                  />
                </Tooltip>
                <Tooltip label="Znovu (Ctrl+Shift+Z)">
                  <IconButton
                    aria-label="Redo"
                    icon={<FaRedo />}
                    size="sm"
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    onClick={redo}
                    isDisabled={!canRedo()}
                  />
                </Tooltip>
                <Button
                  leftIcon={<FaPaintBrush />}
                  size="sm"
                  variant={showStylePanel ? 'solid' : 'outline'}
                  colorScheme={showStylePanel ? 'blue' : 'whiteAlpha'}
                  onClick={() => setShowStylePanel(!showStylePanel)}
                  borderRadius="xl"
                >
                  Vizuální styly
                </Button>
                <Tooltip label={stylePanelRight ? 'Ukotvit vlevo' : 'Ukotvit vpravo'}>
                  <IconButton
                    aria-label="Dock panel"
                    icon={stylePanelRight ? <FaAngleLeft /> : <FaAngleRight />}
                    size="sm"
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    onClick={() => setStylePanelRight(!stylePanelRight)}
                  />
                </Tooltip>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="whiteAlpha"
                  onClick={handleStartBlank}
                >
                  Začít s prázdnou stránkou
                </Button>
                {unsavedCount > 0 && (
                  <Badge 
                    bg="yellow.400" 
                    color="gray.900"
                    fontSize="sm"
                    px={3}
                    py={1.5}
                    borderRadius="full"
                    fontWeight="700"
                    boxShadow="0 2px 8px rgba(250, 204, 21, 0.4)"
                    border="1px solid rgba(255,255,255,0.5)"
                    sx={{
                      '@keyframes bounce': {
                        '0%, 100%': { transform: 'translateY(0)' },
                        '50%': { transform: 'translateY(-2px)' }
                      },
                      animation: 'bounce 1s infinite'
                    }}
                  >
                    {unsavedCount} neuložených změn
                  </Badge>
                )}
                <Button
                  leftIcon={<FiSave />}
                  bgGradient={`linear(135deg, ${secondaryColor}, ${secondaryColor}dd)`}
                  color="white"
                  size="sm"
                  onClick={handleSave}
                  isDisabled={!hasChanges}
                  fontWeight="700"
                  px={6}
                  borderRadius="xl"
                  boxShadow="0 4px 12px rgba(0,0,0,0.2)"
                  border="2px solid rgba(255,255,255,0.2)"
                  _hover={{ 
                    transform: 'translateY(-2px)', 
                    boxShadow: '0 6px 16px rgba(0,0,0,0.25)'
                  }}
                  _active={{ transform: 'translateY(0)' }}
                  transition="all 0.2s"
                >
                  Publikovat
                </Button>
                <IconButton
                  aria-label="Ukončit"
                  icon={<FiX />}
                  size="sm"
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  onClick={handleExitEditing}
                />
              </HStack>
            </Flex>
          </Box>
          {/* Visual Style Panel (anchored, non-movable) */}
          {showStylePanel && (
            <Box
              className="myuibrix-panel"
              position="fixed"
              left={stylePanelRight ? undefined : 0}
              right={stylePanelRight ? 0 : undefined}
              top="72px"
              bottom={0}
              width="380px"
              zIndex={10002}
              overflow="hidden"
              display="flex"
              flexDirection="column"
              fontFamily="var(--chakra-fonts-body)"
              bg="rgba(255, 255, 255, 0.95)"
              backdropFilter="blur(12px) saturate(180%)"
              borderRadius="0"
              boxShadow="0 20px 60px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.2)"
              border="1px solid rgba(255,255,255,0.3)"
              sx={{
                '@keyframes slideInLeft': {
                  from: { opacity: 0, transform: 'translateX(-40px)' },
                  to: { opacity: 1, transform: 'translateX(0)' }
                },
                animation: 'slideInLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <Box 
                className="panel-header"
                bgGradient={`linear(135deg, ${primaryColor}, ${primaryColor}dd)`}
                color="white"
                p={3}
                cursor="default"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexShrink={0}
                borderTopRadius="0"
                boxShadow="0 2px 8px rgba(0,0,0,0.1)"
                borderBottom="1px solid rgba(255,255,255,0.2)"
              >
                <HStack>
                  <Icon as={FaPaintBrush} />
                  <Text fontWeight="bold" fontSize="sm">Vizuální Styly</Text>
                </HStack>
                <IconButton
                  aria-label="Zavřít"
                  icon={<FiX />}
                  size="xs"
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  onClick={() => setShowStylePanel(false)}
                />
              </Box>
              <Box flex="1" overflow="auto">
                {selectedElement ? (
                  <VisualStylePanel
                    elementName={selectedElement}
                    onStyleChange={(styles) => handleStyleChange(selectedElement, styles)}
                    currentStyles={elementStyles[selectedElement]}
                  />
                ) : (
                  <Box p={4} color="gray.600" fontSize="sm">
                    <Text fontWeight="bold" mb={2}>Vyberte sekci</Text>
                    <Text>Vyberte sekci na stránce pro úpravu vizuálních stylů. Klikněte na zvýrazněný překryv sekce nebo vyberte ze seznamu vrstev.</Text>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Floating Control Panel - Minimalist */}
      <Box
        className="myuibrix-toolbar"
        position="fixed"
        left={4}
        bottom={4}
        zIndex={10000}
        bg={isEditing ? 'rgba(255,255,255,0.95)' : primaryColor}
        borderRadius="full"
        boxShadow="0 12px 40px rgba(0,0,0,0.25), 0 4px 16px rgba(0,0,0,0.15)"
        border="2px solid"
        borderColor={isEditing ? selectedBg : primaryColor}
        backdropFilter="blur(12px)"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{ transform: 'scale(1.05)', boxShadow: '0 16px 48px rgba(0,0,0,0.3), 0 6px 20px rgba(0,0,0,0.2)' }}
      >
        <VStack spacing={0}>
          {/* Edit Toggle */}
          <Tooltip label={isEditing ? "Ukončit režim úprav (ESC)" : "Aktivovat MyUIbrix Editor"} placement="right">
            <IconButton
              aria-label="Toggle edit mode"
              icon={isEditing ? <FiX /> : <FiEdit />}
              colorScheme={isEditing ? "red" : "whiteAlpha"}
              size="lg"
              onClick={() => {
                if (isEditing) {
                  handleExitEditing();
                } else {
                  setIsEditing(true);
                }
              }}
              borderRadius="full"
              variant="solid"
              color={isEditing ? undefined : "white"}
              _hover={{
                transform: 'scale(1.1)',
                boxShadow: 'lg',
              }}
              transition="all 0.2s"
            />
          </Tooltip>

          {isEditing && (
            <>
              <Divider my={1} />
              
              {/* Add Element Button */}
              <Tooltip label="Přidat element (A)" placement="right">
                <IconButton
                  aria-label="Přidat element"
                  icon={<FaPlus />}
                  bgGradient={`linear(135deg, ${primaryColor}, ${primaryColor}dd)`}
                  color="white"
                  _hover={{ transform: 'scale(1.15) rotate(90deg)', boxShadow: 'lg' }}
                  size="md"
                  onClick={() => setShowElementPicker(true)}
                  borderRadius="full"
                  boxShadow="0 4px 12px rgba(0,0,0,0.2)"
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                />
              </Tooltip>
              
              <Divider my={1} />
              
              {/* Layers Panel Toggle */}
              <Tooltip label="Vrstvy elementů (L)" placement="right">
                <IconButton
                  aria-label="Zobrazit vrstvy"
                  icon={<FiMove />}
                  bg={showLayersPanel ? secondaryColor : undefined}
                  color={showLayersPanel ? (clubTheme.textOnSecondary || 'black') : undefined}
                  _hover={{ bg: secondaryColor, color: clubTheme.textOnSecondary || 'black' }}
                  size="md"
                  onClick={() => setShowLayersPanel(!showLayersPanel)}
                  borderRadius="full"
                  variant={showLayersPanel ? "solid" : "ghost"}
                  sx={{
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: 'md',
                    }
                  }}
                  transition="all 0.2s"
                />
              </Tooltip>
              
              {hasChanges && (
                <>
                  <Divider my={1} />
                  <Tooltip label="Uložit změny (Ctrl+S)" placement="right">
                    <IconButton
                      aria-label="Uložit"
                      icon={<FiSave />}
                      bgGradient="linear(135deg, green.400, green.600)"
                      color="white"
                      _hover={{ transform: 'scale(1.2)', bgGradient: 'linear(135deg, green.500, green.700)' }}
                      size="md"
                      onClick={handleSave}
                      borderRadius="full"
                      boxShadow="0 0 0 0 rgba(72, 187, 120, 0.4)"
                      transition="all 0.2s"
                      sx={{
                        '@keyframes pulseGreen': {
                          '0%, 100%': { boxShadow: '0 0 0 0 rgba(72, 187, 120, 0.6), 0 4px 12px rgba(72, 187, 120, 0.4)' },
                          '50%': { boxShadow: '0 0 0 10px rgba(72, 187, 120, 0), 0 6px 16px rgba(72, 187, 120, 0.2)' },
                        },
                        animation: 'pulseGreen 2s infinite'
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </>
          )}
        </VStack>
      </Box>

      {/* Contextual Style Selector - Appears near clicked element */}
      {isEditing && selectedElement && elementPosition && (
        <Portal>
          <Box
            className="myuibrix-panel"
            ref={overlayRef}
            position="fixed"
            top={panelPositions.stylePicker.y === 0 ? `${Math.min(elementPosition.top + elementPosition.height + 10, window.innerHeight - 450)}px` : `${panelPositions.stylePicker.y}px`}
            left={panelPositions.stylePicker.x === 0 ? `${Math.min(elementPosition.left, window.innerWidth - 380)}px` : `${panelPositions.stylePicker.x}px`}
            width={`${panelPositions.stylePicker.width}px`}
            height={`${panelPositions.stylePicker.height}px`}
            bg="rgba(255, 255, 255, 0.98)"
            backdropFilter="blur(16px) saturate(180%)"
            borderRadius="2xl"
            boxShadow="0 24px 70px rgba(0,0,0,0.3), 0 10px 30px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.5)"
            border="2px solid"
            borderColor="rgba(255,255,255,0.4)"
            zIndex={10001}
            overflow="hidden"
            onMouseDown={(e) => handlePanelMouseDown('stylePicker', e)}
            cursor={draggingPanel === 'stylePicker' ? 'grabbing' : 'default'}
            fontFamily="var(--chakra-fonts-body)"
            sx={{
              '@keyframes slideInBounce': {
                '0%': { opacity: 0, transform: 'translateY(-30px) scale(0.9)' },
                '60%': { opacity: 1, transform: 'translateY(5px) scale(1.02)' },
                '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
              },
              animation: 'slideInBounce 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: `linear-gradient(90deg, ${selectedBg}, transparent)`,
                opacity: 0.6
              }
            }}
          >
            {/* Header */}
            <Flex
              className="panel-header"
              bgGradient={`linear(135deg, ${selectedBg}, ${selectedBg}dd)`}
              color="white"
              p={4}
              align="center"
              justify="space-between"
              cursor="move"
              borderTopRadius="2xl"
              boxShadow="0 4px 12px rgba(0,0,0,0.1)"
              borderBottom="1px solid rgba(255,255,255,0.2)"
            >
              <HStack>
                <FiSettings />
                <Heading size="sm" textTransform="uppercase" letterSpacing="wider" fontWeight="800">
                  {PREDEFINED_ELEMENTS.find(e => e.name === selectedElement)?.label || selectedElement}
                </Heading>
              </HStack>
              <HStack spacing={1}>
                <Tooltip label="Přesunout nahoru">
                  <IconButton
                    aria-label="Přesunout nahoru"
                    icon={<FiChevronUp />}
                    size="sm"
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    onClick={() => handleMoveUp(selectedElement)}
                    isDisabled={elementOrder.indexOf(selectedElement) === 0}
                  />
                </Tooltip>
                <Tooltip label="Přesunout dolů">
                  <IconButton
                    aria-label="Přesunout dolů"
                    icon={<FiChevronDown />}
                    size="sm"
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    onClick={() => handleMoveDown(selectedElement)}
                    isDisabled={elementOrder.indexOf(selectedElement) === elementOrder.length - 1}
                  />
                </Tooltip>
                <Divider orientation="vertical" h="20px" borderColor="whiteAlpha.400" />
                <Tooltip label="Odebrat element">
                  <IconButton
                    aria-label="Odebrat element"
                    icon={<FiTrash2 />}
                    size="sm"
                    variant="ghost"
                    colorScheme="whiteAlpha"
                    onClick={() => handleRemoveElement(selectedElement)}
                  />
                </Tooltip>
                <IconButton
                  aria-label="Zavřít"
                  icon={<FiX />}
                  size="sm"
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  onClick={() => setSelectedElement(null)}
                  borderRadius="full"
                />
              </HStack>
            </Flex>

            {/* Variant Grid */}
            <VStack align="stretch" p={4} spacing={3} maxH="450px" overflowY="auto">
              {/* Position Info */}
              <Flex justify="space-between" align="center" pb={2}>
                <HStack spacing={2}>
                  <FiMove size={14} />
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                    Pozice {elementOrder.indexOf(selectedElement) + 1} z {elementOrder.length}
                  </Text>
                </HStack>
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Přesunout nahoru"
                    icon={<FiChevronUp />}
                    size="xs"
                    variant="ghost"
                    onClick={() => handleMoveUp(selectedElement)}
                    isDisabled={elementOrder.indexOf(selectedElement) === 0}
                  />
                  <IconButton
                    aria-label="Přesunout dolů"
                    icon={<FiChevronDown />}
                    size="xs"
                    variant="ghost"
                    onClick={() => handleMoveDown(selectedElement)}
                    isDisabled={elementOrder.indexOf(selectedElement) === elementOrder.length - 1}
                  />
                </HStack>
              </Flex>
              
              <Divider />
              
              <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                Vyberte Styl
              </Text>
              
              <SimpleGrid columns={2} spacing={3}>
                {currentVariants.map((variant) => {
                  const isActive = currentVariant === variant.value;
                  return (
                    <Box
                      key={variant.value}
                      p={3}
                      borderRadius="lg"
                      border="2px"
                      borderColor={isActive ? selectedBg : borderColor}
                      bg={isActive ? useColorModeValue('blue.50', 'blue.900') : 'transparent'}
                      cursor="pointer"
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                      _hover={{
                        borderColor: selectedBg,
                        transform: 'translateY(-4px) scale(1.02)',
                        boxShadow: 'lg',
                      }}
                      _active={{
                        transform: 'translateY(-2px) scale(0.98)',
                      }}
                      onClick={() => handleVariantChange(selectedElement, variant.value)}
                      position="relative"
                    >
                      {isActive && (
                        <Badge
                          position="absolute"
                          top={1}
                          right={1}
                          colorScheme="green"
                          fontSize="9px"
                        >
                          AKTIVNÍ
                        </Badge>
                      )}
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" fontSize="sm">
                          {variant.label}
                        </Text>
                        <Text fontSize="xs" color="gray.500" noOfLines={2}>
                          {variant.description}
                        </Text>
                      </VStack>
                    </Box>
                  );
                })}
              </SimpleGrid>

              <Divider />

              {/* Quick Actions */}
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.500">
                  {currentVariants.length} dostupných stylů
                </Text>
                <Badge colorScheme={hasChanges ? 'orange' : 'gray'}>
                  {hasChanges ? 'Změněno' : 'Uloženo'}
                </Badge>
              </HStack>
            </VStack>
            {/* Resize handle */}
            <Box
              position="absolute"
              bottom={0}
              right={0}
              width="24px"
              height="24px"
              cursor="nwse-resize"
              bgGradient="linear(135deg, transparent, rgba(0,0,0,0.15))"
              opacity={0.4}
              _hover={{ opacity: 1, bgGradient: 'linear(135deg, transparent, rgba(0,0,0,0.25))' }}
              onMouseDown={(e) => handleResizeStart('stylePicker', e)}
              sx={{
                clipPath: 'polygon(100% 0, 100% 100%, 0 100%)'
              }}
              transition="all 0.2s"
            />
          </Box>
        </Portal>
      )}

      {/* Unsaved Changes Indicator */}
      {false && isEditing && hasChanges && (
        <Box
          position="fixed"
          top={4}
          right={4}
          zIndex={10000}
          bgGradient="linear(135deg, orange.400, orange.600)"
          color="white"
          px={5}
          py={2.5}
          borderRadius="full"
          boxShadow="0 8px 24px rgba(237, 137, 54, 0.4), 0 4px 12px rgba(0,0,0,0.2)"
          fontSize="sm"
          fontWeight="800"
          display="flex"
          alignItems="center"
          gap={2}
          border="2px solid rgba(255,255,255,0.3)"
          backdropFilter="blur(8px)"
          sx={{
            '@keyframes wiggle': {
              '0%, 100%': { transform: 'rotate(0deg)' },
              '25%': { transform: 'rotate(-3deg)' },
              '75%': { transform: 'rotate(3deg)' }
            },
            animation: 'wiggle 2s infinite'
          }}
        >
          <Icon as={FiAlertTriangle} boxSize={4} />
          Neuložené změny: {Object.keys(localChanges).filter(k => localChanges[k] !== (configs.find(c => c.element_name === k)?.variant)).length}
        </Box>
      )}

      {/* Help Hint */}
      {isEditing && showHelpHint && !selectedElement && (
        <Box
          position="fixed"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="rgba(255, 255, 255, 0.98)"
          backdropFilter="blur(20px) saturate(180%)"
          p={8}
          borderRadius="3xl"
          boxShadow="0 30px 90px rgba(0,0,0,0.3), 0 12px 40px rgba(0,0,0,0.22)"
          border="2px solid"
          borderColor="rgba(255,255,255,0.4)"
          zIndex={9999}
          maxW="450px"
          textAlign="center"
          fontFamily="var(--chakra-fonts-body)"
          sx={{
            '@keyframes fadeInScale': {
              '0%': { opacity: 0, transform: 'translate(-50%, -50%) scale(0.8)' },
              '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
            },
            animation: 'fadeInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
        >
          <IconButton
            aria-label="Zavřít nápovědu"
            icon={<FiX />}
            size="sm"
            position="absolute"
            top={2}
            right={2}
            variant="ghost"
            onClick={() => setShowHelpHint(false)}
          />
          <VStack spacing={3}>
            <Box fontSize="48px">👆</Box>
            <Heading size="md">Klikněte na element pro úpravu</Heading>
            <Text color="gray.500" fontSize="sm">
              • Přejetím myši zvýrazníte sekce
              <br />
              • Klikněte přímo na element pro úpravu stylu
              <br />
              • Použijte tlačítka ⬆️⬇️ pro změnu pozice
              <br />
              • Klikněte na 🗑️ pro odstranění
              <br />
              • Přepněte viewport pro test responzivity
            </Text>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => {
                setShowHelpHint(false);
                setShowElementPicker(true);
              }}
              leftIcon={<FaPlus />}
            >
              Přidat nový element
            </Button>
          </VStack>
        </Box>
      )}

      {/* Grid insertion pickers */}
      {containerGridCols > 1 && (
        <Box p={4} borderBottom="1px" borderColor="gray.100" bg="white">
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" fontWeight="bold" color="gray.600">Vložit do sloupce</Text>
            <HStack spacing={2} flexWrap="wrap">
              {Array.from({ length: containerGridCols }).map((_, col) => (
                <Button
                  key={col}
                  size="sm"
                  variant={pendingInsertIndex != null && (pendingInsertIndex % containerGridCols) === col ? 'solid' : 'outline'}
                  colorScheme={pendingInsertIndex != null && (pendingInsertIndex % containerGridCols) === col ? 'blue' : 'gray'}
                  onClick={(e) => {
                    e.stopPropagation();
                    const idx = computeInsertIndexForColumn(col);
                    setPendingInsertIndex(idx);
                    toast({ title: 'Pozice zvolena', description: `Sloupec ${col + 1}`, status: 'info', duration: 1500 });
                  }}
                >
                  Sloupec {col + 1}
                </Button>
              ))}
              <Button size="sm" variant="ghost" onClick={() => setPendingInsertIndex(null)}>Zrušit pozici</Button>
            </HStack>
          </VStack>
        </Box>
      )}

      {/* Layers Panel - Visual element list with drag-drop */}
      {isEditing && showLayersPanel && (
        <Box
          className="myuibrix-panel"
          position="fixed"
          left={panelPositions.layersPanel.x === 0 ? undefined : `${panelPositions.layersPanel.x}px`}
          right={panelPositions.layersPanel.x === 0 ? 4 : undefined}
          top={panelPositions.layersPanel.y === 0 ? '72px' : `${panelPositions.layersPanel.y}px`}
          bottom={panelPositions.layersPanel.y === 0 ? 4 : undefined}
          transform={undefined}
          width={`${panelPositions.layersPanel.width}px`}
          height={panelPositions.layersPanel.y === 0 ? 'auto' : `${panelPositions.layersPanel.height}px`}
          bg="rgba(255, 255, 255, 0.97)"
          backdropFilter="blur(16px) saturate(180%)"
          borderRadius="2xl"
          boxShadow="0 24px 70px rgba(0,0,0,0.3), 0 10px 30px rgba(0,0,0,0.22)"
          border="2px solid"
          borderColor="rgba(255,255,255,0.4)"
          zIndex={10001}
          overflow="hidden"
          onMouseDown={(e) => handlePanelMouseDown('layersPanel', e)}
          cursor={draggingPanel === 'layersPanel' ? 'grabbing' : 'default'}
          fontFamily="var(--chakra-fonts-body)"
          display="flex"
          flexDirection="column"
          sx={{
            '@keyframes slideInRight': {
              from: { opacity: 0, transform: 'translateX(40px)' },
              to: { opacity: 1, transform: 'translateX(0)' }
            },
            animation: 'slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {/* Header */}
          <Flex
            className="panel-header"
            bgGradient={`linear(135deg, ${secondaryColor}, ${secondaryColor}dd)`}
            color="white"
            p={4}
            align="center"
            justify="space-between"
            cursor="move"
            borderTopRadius="2xl"
            boxShadow="0 4px 12px rgba(0,0,0,0.15)"
            borderBottom="1px solid rgba(255,255,255,0.2)"
          >
            <HStack>
              <FiMove />
              <Heading size="sm" fontWeight="800" letterSpacing="wide">Vrstvy Elementů</Heading>
            </HStack>
            <IconButton
              aria-label="Zavřít"
              icon={<FiX />}
              size="sm"
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={() => setShowLayersPanel(false)}
            />
          </Flex>

          {/* Blank layout action */}
          <Box p={2} borderBottom="1px" borderColor="whiteAlpha.400" bg="whiteAlpha.200">
            <HStack justify="space-between">
              <Text fontSize="xs" opacity={0.85}>Začít s prázdným rozložením</Text>
              <Button size="xs" variant="outline" onClick={handleStartBlank}>
                Začít s prázdným rozložením
              </Button>
            </HStack>
          </Box>

          {/* Layers List */}
          <VStack align="stretch" p={3} spacing={2} flex={1} overflowY="auto">
            {elementOrder.map((elementName, index) => {
              const element = PREDEFINED_ELEMENTS.find(e => e.name === elementName);
              const isVisible = visibleElements.has(elementName);
              const isSelected = selectedElement === elementName;
              
              const isDragging = draggedElement === elementName;
              const isDragOver = dragOverElement === elementName;
              
              return (
                <Box
                  key={elementName}
                  p={3}
                  borderRadius="lg"
                  border="2px"
                  borderColor={isDragOver ? primaryColor : isSelected ? secondaryColor : 'gray.200'}
                  bgGradient={isDragging ? 'linear(to-r, gray.100, gray.200)' : isSelected ? `linear(135deg, ${secondaryColor}15, ${secondaryColor}25)` : isVisible ? 'linear(to-r, white, gray.50)' : 'linear(to-r, gray.100, gray.150)'}
                  cursor={isDragging ? 'grabbing' : 'grab'}
                  opacity={isDragging ? 0.6 : isVisible ? 1 : 0.5}
                  transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                  transform={isDragOver ? 'scale(1.03) translateX(8px)' : undefined}
                  boxShadow={isSelected ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)'}
                  _hover={{
                    borderColor: secondaryColor,
                    transform: isDragOver ? 'scale(1.03) translateX(8px)' : 'translateX(6px) translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
                  }}
                  draggable
                  onDragStart={(e) => {
                    handleDragStart(elementName, e);
                    (e.target as HTMLElement).style.cursor = 'grabbing';
                  }}
                  onDragEnd={(e) => {
                    (e.target as HTMLElement).style.cursor = 'grab';
                    handleDragEnd();
                  }}
                  onDragOver={(e) => handleDragOver(e, elementName)}
                  onDragLeave={(e) => handleDragLeave(e)}
                  onDrop={(e) => handleDrop(e, elementName)}
                  onClick={() => {
                    setSelectedElement(elementName);
                    const el = safeDOM.querySelector(`[data-element="${elementName}"]`);
                    if (el) {
                      const rect = el.getBoundingClientRect();
                      setElementPosition({
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                      });
                    }
                  }}
                >
                  <Flex align="center" justify="space-between">
                    <HStack flex={1} spacing={2}>
                      <Icon 
                        as={FaGripVertical} 
                        boxSize={4} 
                        color="gray.400"
                        cursor="grab"
                        _active={{ cursor: 'grabbing' }}
                      />
                      <Box
                        p={2}
                        bg={isSelected ? secondaryColor : `${secondaryColor}20`}
                        borderRadius="lg"
                        transition="all 0.2s"
                      >
                        <Icon as={element?.icon || FaCube} boxSize={5} color={isSelected ? 'white' : secondaryColor} />
                      </Box>
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontWeight="bold" fontSize="sm">
                          {element?.label || elementName}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Pozice {index + 1}
                        </Text>
                      </VStack>
                    </HStack>
                    
                    <HStack spacing={1}>
                      <IconButton
                        aria-label="Přesunout nahoru"
                        icon={<FiChevronUp />}
                        size="xs"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveUp(elementName);
                        }}
                        isDisabled={index === 0}
                      />
                      <IconButton
                        aria-label="Přesunout dolů"
                        icon={<FiChevronDown />}
                        size="xs"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveDown(elementName);
                        }}
                        isDisabled={index === elementOrder.length - 1}
                      />
                      <IconButton
                        aria-label={isVisible ? "Skrýt" : "Zobrazit"}
                        icon={<FiEyeOff />}
                        size="xs"
                        variant="ghost"
                        colorScheme={isVisible ? "gray" : "red"}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isVisible) {
                            handleRemoveElement(elementName);
                          } else {
                            const newVisible = new Set(visibleElements);
                            newVisible.add(elementName);
                            setVisibleElements(newVisible);
                            setHasChanges(true);
                            // Live preview: show element again
                            if (isEditing) {
                              window.dispatchEvent(new CustomEvent('myuibrix-change', {
                                detail: { elementName, variant: localChanges[elementName], visible: true, previewMode: true }
                              }));
                            }
                            // Restore element display and re-apply current visual order
                            setTimeout(() => {
                              const el = safeDOM.querySelector(`[data-element="${elementName}"]`);
                              if (el) {
                                (el as HTMLElement).style.display = '';
                              }
                              applyVisualReorder(elementOrder);
                            }, 0);
                          }
                        }}
                      />
                    </HStack>
                  </Flex>
                </Box>
              );
            })}
            
            {elementOrder.length === 0 && (
              <Box textAlign="center" py={8}>
                  <Icon as={FaBoxOpen} boxSize={12} color="gray.300" mb={3} />
                  <Text fontSize="sm" color="gray.500" fontWeight="bold">
                    Zatím žádné elementy
                  </Text>
                  <Text fontSize="xs" color="gray.400" mb={3}>
                    Začněte přidáním prvého elementu
                  </Text>
                  <Button
                    size="sm"
                    bg={primaryColor}
                    color="white"
                    _hover={{ opacity: 0.9 }}
                    leftIcon={<FaPlus />}
                    onClick={() => {
                      setShowLayersPanel(false);
                      setShowElementPicker(true);
                    }}
                  >
                    Přidat Element
                  </Button>
              </Box>
            )}
          </VStack>
          {/* Resize handle */}
          <Box
            position="absolute"
            bottom={0}
            right={0}
            width="20px"
            height="20px"
            cursor="nwse-resize"
            bg="gray.400"
            opacity={0.6}
            _hover={{ opacity: 1 }}
            onMouseDown={(e) => handleResizeStart('layersPanel', e)}
            sx={{
              clipPath: 'polygon(100% 0, 100% 100%, 0 100%)'
            }}
          />
        </Box>
      )}

      {/* Element Picker Modal */}
      {showElementPicker && (
        <Portal>
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.700"
            backdropFilter="blur(8px)"
            zIndex={10002}
            onClick={() => {
              setShowElementPicker(false);
              setSearchQuery('');
              setSelectedCategory('all');
            }}
            sx={{
              '@keyframes fadeInBackdrop': {
                from: { opacity: 0 },
                to: { opacity: 1 }
              },
              animation: 'fadeInBackdrop 0.2s ease-out'
            }}
          />
          <Box
            className="myuibrix-panel"
            position="fixed"
            left={panelPositions.elementPicker.x === 0 ? "50%" : `${panelPositions.elementPicker.x}px`}
            top={panelPositions.elementPicker.y === 0 ? "50%" : `${panelPositions.elementPicker.y}px`}
            transform={panelPositions.elementPicker.x === 0 && panelPositions.elementPicker.y === 0 ? "translate(-50%, -50%)" : undefined}
            width={`${panelPositions.elementPicker.width}px`}
            height={`${panelPositions.elementPicker.height}px`}
            maxWidth="90vw"
            bg="rgba(255, 255, 255, 0.98)"
            backdropFilter="blur(20px) saturate(180%)"
            borderRadius="3xl"
            boxShadow="0 40px 120px rgba(0,0,0,0.4), 0 16px 50px rgba(0,0,0,0.3)"
            border="2px solid"
            borderColor="rgba(255,255,255,0.5)"
            zIndex={10003}
            overflow="hidden"
            onMouseDown={(e) => handlePanelMouseDown('elementPicker', e)}
            cursor={draggingPanel === 'elementPicker' ? 'grabbing' : 'default'}
            fontFamily="var(--chakra-fonts-body)"
            sx={{
              '@keyframes modalZoomIn': {
                '0%': { opacity: 0, transform: panelPositions.elementPicker.x === 0 && panelPositions.elementPicker.y === 0 ? 'translate(-50%, -50%) scale(0.85)' : 'scale(0.85)' },
                '100%': { opacity: 1, transform: panelPositions.elementPicker.x === 0 && panelPositions.elementPicker.y === 0 ? 'translate(-50%, -50%) scale(1)' : 'scale(1)' }
              },
              animation: 'modalZoomIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}
          >
            {/* Header */}
            <Flex
              className="panel-header"
              bgGradient={`linear(135deg, ${primaryColor}, ${primaryColor}dd)`}
              color="white"
              p={5}
              align="center"
              justify="space-between"
              cursor="move"
              borderTopRadius="3xl"
              boxShadow="0 6px 16px rgba(0,0,0,0.15)"
              borderBottom="1px solid rgba(255,255,255,0.2)"
            >
              <HStack spacing={3}>
                <Box
                  p={2}
                  bg="whiteAlpha.300"
                  borderRadius="lg"
                  backdropFilter="blur(8px)"
                >
                  <Icon as={FaPlus} boxSize={6} />
                </Box>
                <Heading size="md" fontWeight="900" letterSpacing="tight">Přidat Element</Heading>
              </HStack>
              <IconButton
                aria-label="Zavřít"
                icon={<FiX />}
                size="sm"
                variant="ghost"
                colorScheme="whiteAlpha"
                onClick={() => {
                  setShowElementPicker(false);
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
              />
            </Flex>

            {/* Search and Filter Bar */}
            <Box p={5} borderBottom="1px" borderColor="gray.100" bg="gray.50">
              <VStack spacing={4}>
                <Input
                  placeholder="🔍 Hledat element..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="lg"
                  bg="white"
                  borderRadius="xl"
                  border="2px solid"
                  borderColor="gray.200"
                  _hover={{ borderColor: primaryColor }}
                  _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 3px ${primaryColor}33` }}
                  fontSize="md"
                  fontWeight="500"
                  transition="all 0.2s"
                />
                <HStack spacing={2} w="full" overflowX="auto" pb={1}>
                  {[
                    { value: 'all', label: 'Všechny', icon: FaCube },
                    { value: 'layout', label: 'Rozvržení', icon: FaThLarge },
                    { value: 'content', label: 'Obsah', icon: FaNewspaper },
                    { value: 'media', label: 'Média', icon: FaPhotoVideo },
                    { value: 'interactive', label: 'Interaktivní', icon: FaGamepad },
                  ].map((cat) => (
                    <Button
                      key={cat.value}
                      size="md"
                      variant={selectedCategory === cat.value ? 'solid' : 'outline'}
                      bgGradient={selectedCategory === cat.value ? `linear(135deg, ${primaryColor}, ${primaryColor}dd)` : undefined}
                      color={selectedCategory === cat.value ? 'white' : 'gray.700'}
                      borderColor={selectedCategory === cat.value ? 'transparent' : 'gray.300'}
                      borderRadius="xl"
                      fontWeight="700"
                      boxShadow={selectedCategory === cat.value ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'}
                      _hover={{ 
                        bg: selectedCategory === cat.value ? undefined : 'gray.100',
                        transform: 'translateY(-2px)',
                        boxShadow: selectedCategory === cat.value ? '0 6px 16px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                      onClick={() => setSelectedCategory(cat.value)}
                      leftIcon={<Icon as={cat.icon} />}
                      minW="fit-content"
                      transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                    >
                      {cat.label}
                    </Button>
                  ))}
                </HStack>
              </VStack>
            </Box>

            {/* Element Grid by Category */}
            <VStack align="stretch" p={4} spacing={4} maxH="calc(80vh - 200px)" overflowY="auto">
              {['layout', 'content', 'media', 'interactive'].map((category) => {
                // Filter by category selection
                if (selectedCategory !== 'all' && selectedCategory !== category) return null;
                
                // IMPORTANT: Only show elements that are actually implemented on this page
                const implementedForThisPage = pageType === 'homepage' ? HOMEPAGE_IMPLEMENTED_ELEMENTS : [];
                
                const elements = PREDEFINED_ELEMENTS.filter(e => 
                  e.category === category && 
                  (implementedForThisPage.length === 0 || implementedForThisPage.includes(e.name))
                );
                
                const availableElements = elements.filter(e => {
                  // Filter by search query only; allow duplicates (we'll warn and move existing)
                  if (searchQuery) {
                    const query = searchQuery.toLowerCase();
                    return e.label.toLowerCase().includes(query) || 
                           e.description.toLowerCase().includes(query);
                  }
                  return true;
                });
                
                if (availableElements.length === 0) return null;
                
                const categoryLabels = {
                  layout: 'Rozvržení',
                  content: 'Obsah',
                  media: 'Média',
                  interactive: 'Interaktivní'
                };
                
                return (
                  <Box key={category}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>
                      {categoryLabels[category as keyof typeof categoryLabels]}
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      {availableElements.map((element) => (
                        <Box
                          key={element.name}
                          p={4}
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="gray.200"
                          bg="white"
                          cursor="pointer"
                          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                          _hover={{
                            borderColor: primaryColor,
                            transform: 'translateY(-4px) scale(1.02)',
                            boxShadow: `0 8px 24px rgba(0,0,0,0.12), 0 0 0 2px ${primaryColor}22`,
                            bg: `${primaryColor}05`
                          }}
                          _active={{
                            transform: 'translateY(-2px) scale(1)'
                          }}
                          onClick={() => handleAddElement(element.name)}
                        >
                          <HStack spacing={3}>
                            <Box
                              p={3}
                              bgGradient={`linear(135deg, ${primaryColor}15, ${primaryColor}25)`}
                              borderRadius="xl"
                              transition="all 0.2s"
                            >
                              <Icon as={element.icon} boxSize={6} color={primaryColor} />
                            </Box>
                            <VStack align="start" spacing={0} flex={1}>
                              <Text fontWeight="bold" fontSize="sm">
                                {element.label}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {element.description}
                              </Text>
                            </VStack>
                            <Icon as={FaPlus} color={primaryColor} />
                          </HStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                );
              })}
              
              {PREDEFINED_ELEMENTS.every(e => visibleElements.has(e.name)) && (
                <Box textAlign="center" py={8}>
                  <Icon as={FaSmile} boxSize={8} color={primaryColor} mb={2} />
                  <Text fontSize="lg" fontWeight="bold" mb={2}>
                    Všechny elementy přidány!
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Používáte všechny dostupné elementy
                  </Text>
                </Box>
              )}
              
              {/* No results message */}
              {!PREDEFINED_ELEMENTS.some(e => {
                const matchesCategory = selectedCategory === 'all' || e.category === selectedCategory;
                const matchesSearch = !searchQuery || 
                  e.label.toLowerCase().includes(searchQuery.toLowerCase()) || 
                  e.description.toLowerCase().includes(searchQuery.toLowerCase());
                return !visibleElements.has(e.name) && matchesCategory && matchesSearch;
              }) && !PREDEFINED_ELEMENTS.every(e => visibleElements.has(e.name)) && (
                <Box textAlign="center" py={8}>
                  <Icon as={FaExclamationCircle} boxSize={8} color="gray.400" mb={2} />
                  <Text fontSize="md" fontWeight="bold" mb={1}>
                    Žádné výsledky
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Zkuste změnit vyhledávací dotaz nebo filtr
                  </Text>
                </Box>
              )}
            </VStack>
            {/* Resize handle */}
              <Box
                position="absolute"
                bottom={0}
                right={0}
                width="24px"
                height="24px"
                cursor="nwse-resize"
                bgGradient="linear(135deg, transparent, rgba(0,0,0,0.15))"
                opacity={0.4}
                _hover={{ opacity: 1, bgGradient: 'linear(135deg, transparent, rgba(0,0,0,0.25))' }}
                onMouseDown={(e) => handleResizeStart('elementPicker', e)}
                sx={{
                  clipPath: 'polygon(100% 0, 100% 100%, 0 100%)'
                }}
                transition="all 0.2s"
              />
          </Box>
        </Portal>
      )}
    </>
  );
};

export default MyUIbrixStyleEditor;
